<script setup lang="ts">
import type { AlertVariant } from '@/types/ui';
import { Icon } from '@iconify/vue';
/**
 * UiToast - Design System Toast Notification Component
 *
 * A toast notification that appears temporarily.
 *
 * @example
 * <UiToast
 *   v-model="show"
 *   variant="success"
 *   title="Success!"
 *   message="Your changes have been saved."
 * />
 */
import { onMounted, watch } from 'vue';
import { IconLib } from '@/assets/icon-library';

export interface UiToastProps {
   /** Toast variant */
   variant?: AlertVariant;
   /** Title text */
   title?: string;
   /** Message text */
   message?: string;
   /** Duration in ms (0 = no auto-dismiss) */
   duration?: number;
   /** Position on screen */
   position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
   /** Whether to show close button */
   closable?: boolean;
}

const props = withDefaults(defineProps<UiToastProps>(), {
   variant: 'info',
   duration: 5000,
   position: 'bottom-right',
   closable: true,
});

const emit = defineEmits<{
   close: [];
}>();

const modelValue = defineModel<boolean>({ required: true });

const variantConfig: Record<AlertVariant, { bg: string; border: string; icon: string }> = {
   info: {
      bg: 'bg-white',
      border: 'border-l-4 border-l-blue-500',
      icon: IconLib.info,
   },
   success: {
      bg: 'bg-white',
      border: 'border-l-4 border-l-green-500',
      icon: 'ph:check-circle',
   },
   warning: {
      bg: 'bg-white',
      border: 'border-l-4 border-l-yellow-500',
      icon: IconLib.error.triangle,
   },
   error: {
      bg: 'bg-white',
      border: 'border-l-4 border-l-red-500',
      icon: IconLib.error.circle,
   },
};

const iconColors: Record<AlertVariant, string> = {
   info: 'text-blue-500',
   success: 'text-green-500',
   warning: 'text-yellow-500',
   error: 'text-red-500',
};

const positionClasses: Record<string, string> = {
   'top-right': 'top-4 right-4',
   'top-left': 'top-4 left-4',
   'bottom-right': 'bottom-4 right-4',
   'bottom-left': 'bottom-4 left-4',
   'top-center': 'top-4 left-1/2 -translate-x-1/2',
   'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
};

let timer: ReturnType<typeof setTimeout> | null = null;

function close() {
   modelValue.value = false;
   emit('close');
}

function startTimer() {
   if (props.duration > 0) {
      timer = setTimeout(close, props.duration);
   }
}

function clearTimer() {
   if (timer) {
      clearTimeout(timer);
      timer = null;
   }
}

watch(modelValue, (value) => {
   if (value) {
      startTimer();
   }
   else {
      clearTimer();
   }
});

onMounted(() => {
   if (modelValue.value) {
      startTimer();
   }
});
</script>

<template>
   <Teleport to="body">
      <Transition
         enter-active-class="transition duration-300 ease-out"
         enter-from-class="opacity-0 translate-y-2"
         enter-to-class="opacity-100 translate-y-0"
         leave-active-class="transition duration-200 ease-in"
         leave-from-class="opacity-100 translate-y-0"
         leave-to-class="opacity-0 translate-y-2"
      >
         <div
            v-if="modelValue"
            class="fixed z-[100] w-80 shadow-lg rounded-lg overflow-hidden"
            :class="[variantConfig[variant].bg, variantConfig[variant].border, positionClasses[position]]"
            role="alert"
            @mouseenter="clearTimer"
            @mouseleave="startTimer"
         >
            <div class="flex gap-3 p-4">
               <!-- Icon -->
               <Icon
                  :icon="variantConfig[variant].icon"
                  class="size-5 flex-shrink-0 mt-0.5"
                  :class="iconColors[variant]"
               />

               <!-- Content -->
               <div class="flex-1 min-w-0">
                  <p v-if="title" class="font-semibold text-black">
                     {{ title }}
                  </p>
                  <p class="text-sm text-neutral-dark" :class="{ 'mt-1': title }">
                     <slot>{{ message }}</slot>
                  </p>
               </div>

               <!-- Close button -->
               <button
                  v-if="closable"
                  type="button"
                  class="flex-shrink-0 p-1 -m-1 rounded-md text-neutral-dark hover:text-black hover:bg-neutral-light transition-colors"
                  aria-label="Close"
                  @click="close"
               >
                  <Icon icon="mdi:close" class="size-4" />
               </button>
            </div>
         </div>
      </Transition>
   </Teleport>
</template>
